import { isEmpty, uniqBy } from 'lodash';

import { capitalizeFirstLetter } from 'helpers';
import { useFetch } from 'hooks';
import { appService } from 'services';

/**
 *
 * @param {string[]} filterList : list of filters to fetch for main filters
 */
export const useFilters = (filterList) => {
  const {
    call: tourOperatorsCall,
    data: tourOperatorFilters,
    setData: setTourOperatorFilters,
    loading: loadingTourOperator
  } = useFetch({ withSetState: false });
  const {
    call: countryListCall,
    data: countryListFilters,
    setData: setCountryListFilters,
    loading: loadingCountryList
  } = useFetch({ withSetState: false });
  const {
    call: departureCityCall,
    data: departureCityFilters,
    setData: setDepartureCityFilters,
    loading: loadingDepartureCity
  } = useFetch({ withSetState: false });
  const { call: travelCategoryCall, data: travelCategoryFilters, loading: loadingTravelCategory } = useFetch();
  const { call: packageCall, data: packageFilters, loading: loadingPackage } = useFetch();
  const {
    call: leisureCategoryCall,
    data: leisureCategoryFilters,
    setData: setLeisureCategoryFilters,
    loading: loadingLeisureCategory
  } = useFetch();
  const {
    call: leisureCityCall,
    data: leisureCityFilters,
    setData: setLeisureCityFilters
  } = useFetch({ withSetState: false });
  const {
    call: sportCategoriesCall,
    data: sportCategories,
    setData: setSportCategories,
    loading: loadingSportCategories
  } = useFetch({ withSetState: false });
  const { call: giftcardCategoriesCall, data: giftcardCategories, loading: loadingGiftcardCategories } = useFetch();

  const shouldFetch = (filter) => {
    return filterList.includes(filter);
  };

  const getTravelCategoryFilters = () => {
    travelCategoryCall({ service: 'castelis', path: '/repository/travelCategory', method: 'get' });
  };

  const getPackageFilters = () => {
    packageCall({ service: 'castelis', path: '/repository/packageList', method: 'get' });
  };

  /**
   * Get Filters for leisures results page
   * Filters: leisure categories and leisure cities
   */
  const getLeisureFilters = async () => {
    const [categoriesResponse, departureCitiesResponse] = await Promise.allSettled([
      leisureCategoryCall({ service: 'castelis', path: '/repository/leisureCategory', method: 'get' }),
      leisureCityCall({ service: 'castelis', path: '/repository/leisureCityList', method: 'get' })
    ]);

    if (categoriesResponse?.value?.data?.body) {
      setLeisureCategoryFilters(
        categoriesResponse.value.data.body.map((cat) => ({ ...cat, name: capitalizeFirstLetter(cat.name) }))
      );
    }

    if (departureCitiesResponse?.value?.data?.body) {
      setLeisureCityFilters(
        departureCitiesResponse.value.data.body.map((city) => ({
          value: city.name,
          label: capitalizeFirstLetter(city.name),
          id: city.name
        }))
      );
    }
  };

  const getMainFilters = async () => {
    const responses = await Promise.allSettled([
      shouldFetch('tourOperators') &&
        tourOperatorsCall({ service: 'castelis', path: '/repository/tourOperator', method: 'get' }),
      shouldFetch('countryList') &&
        countryListCall({ service: 'castelis', path: '/repository/countryList', method: 'get' }),
      shouldFetch('departureCityList') &&
        departureCityCall({ service: 'castelis', path: '/repository/departureCityList', method: 'get' })
    ]);

    formatFilters(responses);
  };

  const getSportFilters = async ({ withCityFilter } = {}) => {
    const [categoriesResponse, departureCitiesResponse] = await Promise.allSettled([
      sportCategoriesCall({ service: 'castelis', path: '/repository/sportWellnessCategory', method: 'get' }),
      withCityFilter && leisureCityCall({ service: 'castelis', path: '/repository/leisureCityList', method: 'get' })
    ]);

    const subCategories = uniqBy(categoriesResponse?.value?.data?.body?.[0]?.subCategories, 'id') || [];

    // Format categories for FormSelect
    if (!isEmpty(subCategories)) {
      setSportCategories(
        subCategories.map((subCategory) => ({
          value: subCategory.id,
          label: subCategory.name,
          categoryId: categoriesResponse?.value?.data?.body?.[0]?.id,
          id: subCategory.id
        }))
      );
    }

    if (departureCitiesResponse?.value?.data?.body) {
      setLeisureCityFilters(
        departureCitiesResponse.value.data.body.map((city) => ({
          value: city.name,
          label: capitalizeFirstLetter(city.name),
          id: city.name
        }))
      );
    }
  };

  const getGiftcardCategories = async () => {
    const response = await giftcardCategoriesCall({
      service: appService.getStrapiServiceName(),
      path: '/api/giftcard-categories',
      method: 'get',
      query: {
        pagination: {
          // Set arbitrary high number to get all categories without pagination
          page: 1,
          pageSize: 200
        }
      }
    });
    const categories = response?.data?.body?.data ?? [];

    return categories.sort(compareCategories);
  };

  const compareCategories = (a, b) => {
    const orderA = a?.order ?? null;
    const orderB = b?.order ?? null;

    // If both values are null, sort alphabetically by name
    if (orderA === null && orderB === null) {
      return a.name.localeCompare(b.name);
    }

    // Place null values at the end
    if (orderA === null) return 1;
    if (orderB === null) return -1;

    // If values are different, sort by ascending order
    if (orderA !== orderB) return orderA - orderB;

    // If values are equal, sort alphabetically by name
    return a.name.localeCompare(b.name);
  };

  const formatFilters = (filtersResponses) => {
    const [tourOperators, countryList, departureCityList] = filtersResponses;

    if (tourOperators?.value?.data?.body) {
      const tourOperatorsList = tourOperators.value.data.body.map((p) => ({
        value: p.name,
        label: capitalizeFirstLetter(p.name),
        id: p.id
      }));
      // Add Touloisirs as first provider
      tourOperatorsList.unshift({ label: 'Touloisirs', value: 'touloisirs', id: 'touloisirs' });
      setTourOperatorFilters(tourOperatorsList);
    }
    if (countryList?.value?.data?.body) {
      setCountryListFilters(countryList.value.data.body.map((p) => ({ value: p.name, label: p.name, id: p.id })));
    }
    if (departureCityList?.value?.data?.body) {
      setDepartureCityFilters(
        departureCityList.value.data.body.map((p) => ({ value: p.name, label: p.name, id: p.id }))
      );
    }
  };

  return {
    getMainFilters,
    getLeisureFilters,
    getTravelCategoryFilters,
    getPackageFilters,
    getSportFilters,
    getGiftcardCategories,
    // data
    tourOperatorFilters,
    countryListFilters,
    departureCityFilters,
    travelCategoryFilters,
    packageFilters,
    leisureCategoryFilters,
    leisureCityFilters,
    sportCategories,
    giftcardCategories,
    // loading status
    loadingTourOperator,
    loadingCountryList,
    loadingDepartureCity,
    loadingTravelCategory,
    loadingPackage,
    loadingLeisureCategory,
    loadingSportCategories,
    loadingGiftcardCategories
  };
};
